<template>
  <b-modal
    v-model="isOpen"
    title="Gerenciar Locais"
    size="lg"
    hide-footer
    centered
    @hidden="onClose"
  >
    <div class="mb-3">
      <div class="row">
        <div class="col">
          <input
            type="text"
            class="form-control"
            v-model="newLocationName"
            placeholder="Nome do novo local..."
            @keyup.enter="addLocation"
          />
        </div>
        <div class="col-auto">
          <button
            class="btn btn-primary"
            @click="addLocation"
            :disabled="!newLocationName.trim() || saving"
          >
            <i class="bx bx-plus"></i> Adicionar
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" small></b-spinner>
      <span class="ms-2">Carregando...</span>
    </div>

    <div v-else>
      <div v-if="locations.length === 0" class="text-center py-4 text-muted">
        Nenhum local cadastrado
      </div>

      <table v-else class="table table-bordered">
        <thead>
          <tr>
            <th>Nome</th>
            <th width="100">Status</th>
            <th width="120">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="location in locations" :key="location.id">
            <td>
              <input
                v-if="editingId === location.id"
                type="text"
                class="form-control form-control-sm"
                v-model="editingName"
                @keyup.enter="saveEdit(location)"
                @keyup.esc="cancelEdit"
              />
              <span v-else>{{ location.name }}</span>
            </td>
            <td class="text-center">
              <span
                class="badge"
                :class="location.is_active ? 'badge-success' : 'badge-secondary'"
              >
                {{ location.is_active ? 'Ativo' : 'Inativo' }}
              </span>
            </td>
            <td>
              <div class="d-flex gap-1">
                <template v-if="editingId === location.id">
                  <button
                    class="btn btn-sm btn-success"
                    @click="saveEdit(location)"
                    :disabled="saving"
                  >
                    <i class="bx bx-check"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-secondary"
                    @click="cancelEdit"
                  >
                    <i class="bx bx-x"></i>
                  </button>
                </template>
                <template v-else>
                  <button
                    class="btn btn-sm btn-outline-primary"
                    @click="startEdit(location)"
                    title="Editar"
                  >
                    <i class="bx bx-edit"></i>
                  </button>
                  <button
                    class="btn btn-sm"
                    :class="location.is_active ? 'btn-outline-warning' : 'btn-outline-success'"
                    @click="toggleActive(location)"
                    :title="location.is_active ? 'Desativar' : 'Ativar'"
                  >
                    <i :class="location.is_active ? 'bx bx-hide' : 'bx bx-show'"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    @click="confirmDelete(location)"
                    title="Excluir"
                  >
                    <i class="bx bx-trash"></i>
                  </button>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </b-modal>
</template>

<script>
import {
  fetchLocations,
  createLocation,
  updateLocation,
  deleteLocation
} from '@/modules/storage/service/storageService'
import { successMessage, errorMessage, confirmAction } from '@/components/alertsMessages'

export default {
  name: 'LocationsModal',
  data: function () {
    return {
      isOpen: false,
      loading: false,
      saving: false,
      locations: [],
      newLocationName: '',
      editingId: null,
      editingName: ''
    }
  },
  methods: {
    open: function () {
      this.isOpen = true
      this.loadLocations()
    },
    close: function () {
      this.isOpen = false
    },
    onClose: function () {
      this.editingId = null
      this.editingName = ''
      this.newLocationName = ''
    },
    loadLocations: function () {
      var self = this
      self.loading = true

      fetchLocations()
        .then(function (response) {
          self.loading = false
          self.locations = response.data || response
        })
        .catch(function (error) {
          self.loading = false
          console.error('Erro ao carregar locais:', error)
          errorMessage('Erro ao carregar locais')
        })
    },
    addLocation: function () {
      var self = this
      var name = self.newLocationName.trim()

      if (!name) return

      self.saving = true

      createLocation({ name: name, is_active: true })
        .then(function () {
          self.saving = false
          self.newLocationName = ''
          self.loadLocations()
          successMessage('Local adicionado com sucesso')
        })
        .catch(function (error) {
          self.saving = false
          console.error('Erro ao criar local:', error)
          errorMessage('Erro ao criar local')
        })
    },
    startEdit: function (location) {
      this.editingId = location.id
      this.editingName = location.name
    },
    cancelEdit: function () {
      this.editingId = null
      this.editingName = ''
    },
    saveEdit: function (location) {
      var self = this
      var name = self.editingName.trim()

      if (!name) return

      self.saving = true

      updateLocation(location.id, { name: name })
        .then(function () {
          self.saving = false
          self.editingId = null
          self.editingName = ''
          self.loadLocations()
          successMessage('Local atualizado com sucesso')
        })
        .catch(function (error) {
          self.saving = false
          console.error('Erro ao atualizar local:', error)
          errorMessage('Erro ao atualizar local')
        })
    },
    toggleActive: function (location) {
      var self = this

      updateLocation(location.id, { name: location.name, is_active: !location.is_active })
        .then(function () {
          self.loadLocations()
          successMessage(location.is_active ? 'Local desativado' : 'Local ativado')
        })
        .catch(function (error) {
          console.error('Erro ao atualizar local:', error)
          errorMessage('Erro ao atualizar local')
        })
    },
    confirmDelete: function (location) {
      var self = this

      confirmAction('Excluir Local', 'Tem certeza que deseja excluir o local "' + location.name + '"?')
        .then(function (confirmed) {
          if (confirmed) {
            self.deleteLocation(location)
          }
        })
    },
    deleteLocation: function (location) {
      var self = this

      deleteLocation(location.id)
        .then(function () {
          self.loadLocations()
          successMessage('Local excluído com sucesso')
        })
        .catch(function (error) {
          console.error('Erro ao excluir local:', error)
          errorMessage('Erro ao excluir local. Verifique se não há itens associados.')
        })
    }
  }
}
</script>

<style scoped>
.gap-1 {
  gap: 0.25rem;
}
</style>
